import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Avatar } from '@/ui/Avatar';
import { DialogBody, DialogDone, DialogFoot, DialogHead, confirmDialog } from '@/ui/Dialog';
import { Icon } from '@/ui/Icon';
import { Callout, Chip, Loading, Progress, type ChipTone } from '@/ui/bits';
import { errorMessage } from '@/lib/api';
import { formatDayTime, formatNumber, formatTime, formatWhen, plural } from '@/lib/format';
import { openDialog } from '@/lib/overlays';
import { queryClient } from '@/lib/queryClient';
import { createStore, useStore } from '@/lib/store';
import { toast } from '@/lib/toast';
import { intervalLabel, type ListCopy, type ListSendItem, type OutboxStatus, type ScheduledEntry } from '@shared/mail';
import { cancelListSend, fetchListSend, fetchScheduled, keys } from './data';
import { whenPhrase } from './logic';

// How a list send is getting on: a small bar while copies go out, and a dialog with every copy and a way to stop the rest.
// The copy rows, their status words and the stop action are shared with the list send's pane in the Inbox.

const watch = createStore<{ ids: readonly string[]; hidden: readonly string[] }>({ ids: [], hidden: [] });

/** Shows the progress bar for a list send started here. */
export function watchListSend(id: string): void {
  watch.set((s) => ({ ids: [...s.ids, id], hidden: s.hidden.filter((h) => h !== id) }));
}

function hide(id: string): void {
  watch.set((s) => ({ hidden: [...s.hidden, id] }));
}

type ListEntry = Extract<ScheduledEntry, { kind: 'list' }>;
const isList = (e: ScheduledEntry): e is ListEntry => e.kind === 'list';
const finished = (s: ListSendItem['status']) => s === 'done' || s === 'cancelled';

function statusLine(l: ListSendItem): string {
  const out = l.sent + l.failed;
  if (l.status === 'scheduled') return `${plural(l.total, 'copy', 'copies')} · starts ${whenPhrase(new Date(l.startsAt))}`;
  if (l.status === 'sending') return `${formatNumber(l.sent)} of ${formatNumber(l.total)} sent${l.nextAt ? ` · next at ${formatTime(l.nextAt)}` : ''}`;
  if (l.status === 'cancelled') return `Cancelled · ${formatNumber(l.sent)} of ${formatNumber(l.total)} sent`;
  if (l.failed) return `${formatNumber(l.sent)} sent, ${formatNumber(l.failed)} not sent`;
  return `All ${formatNumber(out)} sent`;
}

function done(l: ListSendItem): number {
  return l.total ? ((l.sent + l.failed + l.cancelled) / l.total) * 100 : 100;
}

export function ListProgress() {
  const ids = useStore(watch, (s) => s.ids);
  const hidden = useStore(watch, (s) => s.hidden);
  const { data } = useQuery({
    queryKey: keys.running,
    queryFn: fetchScheduled,
    staleTime: 15_000,
    refetchInterval: (q) => ((q.state.data ?? []).some((e) => isList(e) && e.mine && e.status === 'sending') ? 20_000 : false),
  });
  const lists = (data ?? []).filter(isList).filter((l) => l.mine && !hidden.includes(l.id) && (l.status === 'sending' || ids.includes(l.id)));
  if (!lists.length) return null;
  return (
    <div className="ls-dock" role="region" aria-label="List sends">
      {lists.slice(0, 3).map((l) => (
        <div key={l.id} className="ls-dock-it">
          <span className="ls-dock-ic" aria-hidden="true">
            <Icon name={l.status === 'scheduled' ? 'clock' : finished(l.status) ? 'check-circle' : 'send'} />
          </span>
          <span className="grow">
            <b>{l.listName ?? l.subject}</b>
            <span>{statusLine(l)}</span>
            {l.status === 'sending' ? <Progress value={done(l)} label={`${l.listName ?? l.subject}: ${statusLine(l)}`} /> : null}
          </span>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => openListProgress(l.id)}>
            Details
          </button>
          {finished(l.status) || l.status === 'scheduled' ? (
            <button type="button" className="icon-btn xs" aria-label="Hide" data-tip="Hide" onClick={() => hide(l.id)}>
              <Icon name="x" />
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function openListProgress(id: string): void {
  openDialog({ label: 'List send', size: 'mid', render: (close) => <ListSendDetailView id={id} close={close} /> });
}

/** What each copy's status reads and its chip's tone. */
export const COPY_STATUS: Record<OutboxStatus, { label: string; tone: ChipTone }> = {
  scheduled: { label: 'Waiting', tone: 'plain' },
  sending: { label: 'Sending', tone: 'info' },
  sent: { label: 'Sent', tone: 'success' },
  failed: { label: 'Not sent', tone: 'danger' },
  cancelled: { label: 'Cancelled', tone: 'plain' },
};

/** One row per copy: who, when, opens and status. Names link to the person when `onOpenPerson` is given. */
export function CopyList({ copies, onOpenPerson, children }: { copies: ListCopy[]; onOpenPerson?: (personId: string) => void; children?: ReactNode }) {
  return (
    <div className="ls-copies" role="list" aria-label="Copies">
      {copies.map((c) => (
        <div key={c.outboxId} role="listitem" className="ls-copy">
          <Avatar name={c.name} size="xs" />
          {c.personId && onOpenPerson ? (
            <button type="button" className="link ls-name" onClick={() => onOpenPerson(c.personId!)}>
              {c.name}
            </button>
          ) : (
            <b className="ls-name">{c.name}</b>
          )}
          <span className="ls-mail">{c.email}</span>
          <span className="spacer" />
          {c.error ? (
            <span className="hint" data-tip={c.error}>
              <Icon name="warn" className="i-sm" />
            </span>
          ) : null}
          {c.openCount ? <span className="hint">{plural(c.openCount, 'open')}</span> : null}
          {c.status === 'sent' || c.status === 'scheduled' ? <span className="hint tnum">{formatWhen(c.sendAt)}</span> : null}
          <Chip tone={COPY_STATUS[c.status].tone}>{COPY_STATUS[c.status].label}</Chip>
        </div>
      ))}
      {children}
    </div>
  );
}

/** Asks first, then stops the copies of a list send that have not gone out. Resolves true once stopped. */
export async function stopListSend(l: Pick<ListSendItem, 'id' | 'waiting'>): Promise<boolean> {
  let sent = 0;
  const ok = await confirmDialog({
    title: 'Stop this list send?',
    body: `${plural(l.waiting, 'copy', 'copies')} still waiting will not be sent. Copies already sent stay sent.`,
    confirmLabel: 'Stop sending',
    cancelLabel: 'Keep sending',
    icon: 'x-circle',
    danger: true,
    action: async () => {
      sent = (await cancelListSend(l.id)).sent;
    },
  });
  if (!ok) return false;
  void queryClient.invalidateQueries({ queryKey: ['inbox'] });
  toast(sent ? `List send stopped. ${plural(sent, 'copy', 'copies')} already sent` : 'List send stopped before the first email', { icon: 'x-circle' });
  return true;
}

/** The stop button, in the same place and words in the Inbox pane and this dialog. */
export function StopListSendButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="btn btn-tile" onClick={onClick}>
      <Icon name="x-circle" />
      Stop sending
    </button>
  );
}

function ListSendDetailView({ id, close }: { id: string; close: () => void }) {
  const q = useQuery({
    queryKey: keys.listSend(id),
    queryFn: () => fetchListSend(id),
    refetchInterval: (query) => (query.state.data && !finished(query.state.data.status) ? 15_000 : false),
  });

  const l = q.data;
  if (!l) {
    return (
      <>
        <DialogHead title="List send" onClose={close} />
        <DialogBody>
          {q.isError ? (
            <Callout
              tone="danger"
              role="alert"
              title="The list send is not loading"
              actions={
                <button type="button" className="btn btn-sm btn-tile" onClick={() => void q.refetch()}>
                  <Icon name="refresh" />
                  Try again
                </button>
              }
            >
              {errorMessage(q.error)}
            </Callout>
          ) : (
            <Loading label="Loading the list send" />
          )}
        </DialogBody>
      </>
    );
  }

  const live = !finished(l.status);
  // The question takes this dialog's place, so the dialog comes back afterwards whatever the answer.
  const stop = () => void stopListSend(l).then(() => openListProgress(id));
  return (
    <>
      <DialogHead
        title={l.listName ? `Send to ${l.listName}` : l.subject}
        sub={`From ${l.mailbox?.address ?? 'a removed inbox'} · ${intervalLabel(l.intervalSeconds).toLowerCase()} · started ${formatDayTime(l.startsAt)}`}
        onClose={close}
      />
      <DialogBody>
        <div className="lsp-top">
          <div className="lsp-line">
            <b>{statusLine(l)}</b>
            {l.finishAt && live ? <span className="muted">Last copy around {formatTime(l.finishAt)}</span> : null}
          </div>
          <Progress value={done(l)} label="Copies sent" />
          <div className="lsp-counts">
            <Chip tone="success" icon="check">
              {formatNumber(l.sent)} sent
            </Chip>
            {l.waiting || live ? (
              <Chip tone="plain" icon="clock">
                {formatNumber(l.waiting)} waiting
              </Chip>
            ) : null}
            {l.failed ? (
              <Chip tone="danger" icon="warn">
                {formatNumber(l.failed)} not sent
              </Chip>
            ) : null}
            {l.cancelled ? (
              <Chip tone="plain" icon="x">
                {formatNumber(l.cancelled)} cancelled
              </Chip>
            ) : null}
            {l.trackOpens ? (
              <Chip icon="eye">
                {plural(l.opens, 'open')}
              </Chip>
            ) : null}
            {l.skipped.length ? <Chip icon="users">{formatNumber(l.skipped.length)} skipped</Chip> : null}
          </div>
        </div>
        <CopyList copies={l.copies} />
      </DialogBody>
      <DialogFoot>
        <span className="spacer" />
        {live ? <StopListSendButton onClick={stop} /> : null}
        <DialogDone onClick={close} />
      </DialogFoot>
    </>
  );
}
